export type ActionOutcome = 'ready' | 'waiting' | 'finished' | 'rejected' | 'failed' | 'navigation' | 'interrupted';
export interface ActionHttpAttempt {
  requestId: string;
  kind: 'action' | 'refresh';
  durationMilliseconds: number;
  statusCode?: number;
}
export interface ActionInteractionReport {
  interactionId: string;
  gameId?: string;
  outcome: ActionOutcome;
  totalDurationMilliseconds: number;
  stages: { name: string; durationMilliseconds: number }[];
  attempts: ActionHttpAttempt[];
  inputSource: 'click' | 'keyboard' | 'programmatic';
  hidden: boolean;
}
type Sender = (report: ActionInteractionReport) => Promise<void>;
export function newPerformanceId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
const queue: { report: ActionInteractionReport; send: Sender }[] = [];
let sending = false;

function enqueue(report: ActionInteractionReport, send: Sender) {
  if (!report.attempts.some(attempt => attempt.kind === 'action')) return;
  if (queue.length === 20) queue.shift();
  queue.push({ report, send });
  if (sending) return;
  sending = true;
  // A task after readiness: uploading must never hold the action loading guard.
  setTimeout(async () => {
    try {
      while (queue.length) {
        const entry = queue.shift()!;
        try { await entry.send(entry.report); } catch { /* Best effort; no gameplay errors or retries. */ }
      }
    } finally { sending = false; }
  }, 0);
}

let origin: { timestamp: number; source: 'click' | 'keyboard' } | undefined;
/** Capture at document level so portalled action dialogs share the same input boundary. */
export function observeActionInputs(): () => void {
  const capture = (event: Event) => {
    if (event instanceof KeyboardEvent && !['Enter', ' '].includes(event.key)) return;
    const now = performance.now();
    const keyboard = event.type === 'keydown' || (event instanceof MouseEvent && event.isTrusted && event.detail === 0);
    const captured = { timestamp: event.timeStamp >= 0 && event.timeStamp <= now ? event.timeStamp : now,
      source: (keyboard ? 'keyboard' : 'click') as 'click' | 'keyboard' };
    origin = captured;
    // Trusted events can checkpoint microtasks between native capture and React's handler.
    // Keep the origin for the whole task, then discard unsubmitted confirmation clicks.
    setTimeout(() => { if (origin === captured) origin = undefined; }, 0);
  };
  document.addEventListener('click', capture, true);
  document.addEventListener('keydown', capture, true);
  return () => {
    document.removeEventListener('click', capture, true);
    document.removeEventListener('keydown', capture, true);
    origin = undefined;
  };
}

export class ActionTrace {
  readonly interactionId = newPerformanceId();
  readonly started = origin?.timestamp ?? performance.now();
  readonly inputSource = origin?.source ?? 'programmatic';
  readonly stages = new Map<string, number>();
  readonly attempts: ActionHttpAttempt[] = [];
  private readonly pendingAttempts = new Map<ActionHttpAttempt, number>();
  outcome?: ActionOutcome;
  private applyStarted?: number;
  private controlsAt?: number;
  private frame?: number;
  private finished = false;
  private hidden = document.visibilityState !== 'visible';
  private readonly visibility = () => { if (document.visibilityState !== 'visible') this.hidden = true; };
  private readonly pagehide = () => this.finish('interrupted');

  constructor(readonly gameId: string | undefined, private readonly send: Sender,
    private readonly onFinished: () => void = () => {}) {
    document.addEventListener('visibilitychange', this.visibility);
    window.addEventListener('pagehide', this.pagehide);
  }

  duration(name: string, start: number) { this.stages.set(name, Math.max(0, performance.now() - start)); }
  beginAttempt(kind: ActionHttpAttempt['kind'], requestId: string) {
    const attempt: ActionHttpAttempt = { kind, requestId, durationMilliseconds: 0 };
    this.attempts.push(attempt);
    this.pendingAttempts.set(attempt, performance.now());
    return attempt;
  }
  completeAttempt(attempt: ActionHttpAttempt, response?: Response) {
    attempt.durationMilliseconds = Math.max(0, performance.now() - this.pendingAttempts.get(attempt)!);
    this.pendingAttempts.delete(attempt);
    if (response) {
      attempt.requestId = response.headers?.get('X-Request-Id') || attempt.requestId;
      attempt.statusCode = response.status;
    }
  }
  requestStarted() { this.duration('Click to request', this.started); }
  applyingState() { this.applyStarted = performance.now(); }
  controlsEnabled() {
    if (this.controlsAt !== undefined || this.applyStarted === undefined) return;
    this.duration('State apply to controls enabled', this.applyStarted);
    this.controlsAt = performance.now();
  }
  presentationReady(outcome: ActionOutcome) {
    if (this.finished || this.frame !== undefined || this.controlsAt === undefined) return;
    this.outcome = this.outcome ?? outcome;
    // Two callbacks straddle a rendering opportunity, not a guarantee of physical screen paint.
    this.frame = requestAnimationFrame(() => {
      this.frame = requestAnimationFrame(() => {
        this.duration('Controls enabled to rendering opportunity', this.controlsAt!);
        this.finish(this.outcome!);
      });
    });
  }
  finish(outcome: ActionOutcome) {
    if (this.finished) return;
    this.finished = true;
    if (this.frame !== undefined) cancelAnimationFrame(this.frame);
    document.removeEventListener('visibilitychange', this.visibility);
    window.removeEventListener('pagehide', this.pagehide);
    const ended = performance.now();
    const report: ActionInteractionReport = { interactionId: this.interactionId, gameId: this.gameId,
      outcome, totalDurationMilliseconds: Math.max(0, ended - this.started),
      stages: Array.from(this.stages, ([name, durationMilliseconds]) => ({ name, durationMilliseconds })),
      attempts: this.attempts.map(attempt => ({ ...attempt, durationMilliseconds: this.pendingAttempts.has(attempt)
        ? Math.max(0, ended - this.pendingAttempts.get(attempt)!) : attempt.durationMilliseconds })),
      inputSource: this.inputSource, hidden: this.hidden };
    enqueue(report, this.send);
    this.onFinished();
  }
}
